import { CatalystInsight } from '@api';
import { BAND_CHART_MARGIN_BOTTOM, BAND_CHART_RIGHT_PADDING } from '@design/charts/band-chart/BandChart';
import styles from '@terminal/modules/analytics-line-chart/components/insights-overlay/InsightsOverlay.module.scss';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { CatalystInsightIcon } from '@terminal/widgets/catalyst-insights-drawer/catalyst-insight-icon/CatalystInsightIcon';
import * as d3 from 'd3';
import React, { useMemo } from 'react';

const THEME_PERF_BAND_CHART_PLOT_HEIGHT_PX = 240;
const ICON_SIZE = 20;
const ICON_TOP_OFFSET = 4;

export interface BandChartTsPoint {
  date: Date;
}

export interface BandChartInsightsOverlayProps {
  insights: CatalystInsight[];
  chartWidth: number;
  tsData: BandChartTsPoint[];
}

interface GroupedInsight {
  dateKey: string;
  insights: CatalystInsight[];
  xPosition: number;
}

const findClosestDateIndex = (
  tsData: BandChartTsPoint[],
  target: Date
): number => {
  if (tsData.length === 0) {
    return -1;
  }

  const targetTime = target.getTime();
  const firstTime = tsData[0].date.getTime();
  const lastTime = tsData[tsData.length - 1].date.getTime();

  if (targetTime < firstTime || targetTime > lastTime) {
    return -1;
  }

  let bestIdx = 0;
  let bestDiff = Infinity;

  for (let i = 0; i < tsData.length; i++) {
    const diff = Math.abs(tsData[i].date.getTime() - targetTime);

    if (diff < bestDiff) {
      bestDiff = diff;
      bestIdx = i;
    }
  }

  return bestIdx;
};

const groupInsightsByDateAndIndex = (
  insights: CatalystInsight[],
  tsData: BandChartTsPoint[],
  xScale: d3.ScaleLinear<number, number>
): GroupedInsight[] => {
  const grouped = new Map<string, CatalystInsight[]>();

  insights.forEach(insight => {
    const date = new Date(insight.created_at);
    const dateKey = date.toISOString().split('T')[0];

    if (!grouped.has(dateKey)) {
      grouped.set(dateKey, []);
    }

    grouped.get(dateKey)?.push(insight);
  });

  return Array.from(grouped.entries())
    .map(([dateKey, groupInsights]) => {
      const index = findClosestDateIndex(tsData, new Date(dateKey));

      return {
        dateKey,
        insights: groupInsights,
        xPosition: xScale(index),
      };
    })
    .filter(group => group.xPosition >= 0);
};

export const BandChartInsightsOverlay = ({
  insights,
  chartWidth,
  tsData,
}: BandChartInsightsOverlayProps) => {
  const n = tsData.length;
  const innerWidth = Math.max(chartWidth - BAND_CHART_RIGHT_PADDING, 0);

  const xScale = useMemo(() => {
    const domainMax = Math.max(n - 1, 0);

    return d3.scaleLinear().domain([0, domainMax]).range([0, innerWidth]);
  }, [n, innerWidth]);

  const groupedInsights = useMemo(
    () => groupInsightsByDateAndIndex(insights, tsData, xScale),
    [insights, tsData, xScale]
  );

  return (
    <div
      className={styles.overlayContainer}
      data-testid="band-chart-insights-overlay"
    >
      {groupedInsights.map(group => (
        <React.Fragment key={group.dateKey}>
          {group.insights.map((insight, idx) => {
            const top =
              THEME_PERF_BAND_CHART_PLOT_HEIGHT_PX -
              BAND_CHART_MARGIN_BOTTOM -
              ICON_SIZE -
              ICON_TOP_OFFSET -
              idx * (ICON_SIZE + 2);

            return (
              <div
                key={insight.id}
                className={styles.insightIconWrapper}
                style={{ left: group.xPosition, top }}
              >
                <CatalystInsightIcon
                  catalystInsightId={insight.id}
                  identifier={insight.identifier}
                  subCategory={insight.sub_category}
                  customSize={{ width: ICON_SIZE, height: ICON_SIZE }}
                  isBordered
                  source={POSTHOG_TRACKING_SOURCES.MARKET_CATALYST_DRAWER}
                  category={insight.category}
                />
              </div>
            );
          })}
        </React.Fragment>
      ))}
    </div>
  );
};
